import classNames from 'classnames';
import React from 'react';
import HoverContentPreview from '../../post/HoverContentPreview';
import EmbedComponent from './EmbedComponent';
import * as styles from './EmbedContent.module.scss';
import EmbedPostContent from './EmbedPostContent';
import ExternalEmbedFooter, {
  embedsWithNativeFooter,
} from './ExternalEmbedFooter';
import type { PreviewMode } from './matchEmbed';

interface EmbedContentWrapperProps {
  children: React.ReactNode;
  type: string;
}

const EmbedContentWrapper = ({ children, type }: EmbedContentWrapperProps) => (
  <div
    className={classNames(
      'embed-content',
      styles.wrapper,
      styles[`type-${type}`],
    )}
  >
    {children}
  </div>
);

interface EmbedContentContainerProps {
  children: React.ReactNode;
  readOnly: boolean;
  disableContentSelection?: boolean;
}

const EmbedContentContainer = ({
  children,
  readOnly,
  disableContentSelection,
}: EmbedContentContainerProps) => (
  <HoverContentPreview.DisableHoverBoundary
    className={classNames(styles.container, {
      [styles.editable]: !readOnly,
      [styles.disableContentSelection]: disableContentSelection,
    })}
  >
    {children}
  </HoverContentPreview.DisableHoverBoundary>
);

interface EmbedContentProps {
  id: string;
  type: string;
  params?: string;
  readOnly: boolean;
  disableContentSelection?: boolean;
  selected?: boolean;
  previewMode?: PreviewMode;
}

const EmbedContent = ({
  id,
  type,
  params,
  readOnly,
  disableContentSelection,
  selected,
  previewMode,
}: EmbedContentProps) => {
  if (type === 'post') {
    return (
      <EmbedContentWrapper type={type}>
        <EmbedContentContainer
          readOnly={readOnly}
          disableContentSelection={disableContentSelection}
        >
          <EmbedPostContent id={id} readOnly={readOnly} params={params} />
        </EmbedContentContainer>
      </EmbedContentWrapper>
    );
  }

  const hasNativeFooter = embedsWithNativeFooter.includes(type);

  const contentClassName = classNames(styles.content, {
    [styles.hasNativeFooter]: hasNativeFooter,
  });

  return (
    <EmbedContentWrapper type={type}>
      <EmbedContentContainer
        readOnly={readOnly}
        disableContentSelection={disableContentSelection}
      >
        <EmbedComponent
          className={contentClassName}
          id={id}
          type={type}
          params={params}
          readOnly={readOnly}
          previewMode={previewMode}
          selected={selected}
        />
        <ExternalEmbedFooter
          id={id}
          type={type}
          params={params}
          className={styles.footer}
        />
      </EmbedContentContainer>
    </EmbedContentWrapper>
  );
};

export default EmbedContent;
